import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import useIsLoggedIn, { sessionJustExpired } from './isLoggedIn';
import { useSelector } from 'react-redux';
import { selectActiveRole } from './slices/userRoleSlice';

/**
 * Helper function to check if a user with a given primary role can access a target role
 * @param {string} primaryRole - The user's primary role
 * @param {string} targetRole - The role to check access for
 * @returns {boolean} Whether the user has access to the target role
 */
function hasRoleAccess(primaryRole, targetRole) {
  if (primaryRole === 'admin') {
    return true; // Admin has access to everything
  } else if (primaryRole === 'bookCreator') {
    return targetRole === 'bookCreator' || targetRole === 'voiceActor';
  } else if (primaryRole === 'voiceActor') {
    return targetRole === 'voiceActor';
  }
  return false;
}

/**
 * ⭐⭐ THE THIRD WAY OUT OF A SESSION, AND THE ONE THAT ACTUALLY HAPPENS (2026-10-26).
 *
 * There were two documented exits: the 401 eject in apiClient, which does
 * window.location.assign, and Logout, which was given one on the 25th. This was the third, and
 * it is the common one — a laptop sleeps, the token lapses, and on wake useIsLoggedIn refuses it
 * at render time before any request goes out. It redirected with <Navigate>, a ROUTER
 * navigation, and that single choice produced four symptoms at once:
 *
 *   ⛔ no document load, so a password manager sees no new form — no autofill, empty fields
 *   ⛔ no 401, so no breadcrumb, so the login page could not say why it was showing
 *   ⛔ no page load, so the Redux store carried the dead session into the login form
 *   ⛔ React state survived, so whatever was in the inputs stayed there
 *
 * The operator's console on 2026-10-26 is the record: ERR_NETWORK_IO_SUSPENDED (the Mac asleep),
 * six SSE reconnects on wake, then three "User not authenticated, redirecting to login" — and
 * an empty breadcrumb history beside it.
 *
 * ⓘ AN EXPIRY IS NOT A GUEST. Someone who was never signed in following a protected link
 * should just be routed; there is nothing to clear and nothing to explain, and a full reload
 * there would be a worse experience for the commoner case. sessionJustExpired() is what tells
 * them apart, since the token itself is gone by the time anyone can look.
 */
function SessionRedirect({ to }) {
  const expired = sessionJustExpired();

  useEffect(() => {
    // ⚠️ IN AN EFFECT, NOT IN RENDER. Navigating while rendering is a side effect in the one
    // place React forbids it, and this component is rendered from inside a route tree.
    if (expired) window.location.assign(to);
  }, [expired, to]);

  // Nothing to show for the instant before the document is replaced.
  return expired ? null : <Navigate to={to} replace />;
}

/**
 * Protected route component that checks if the user is authenticated
 * and has the required role before rendering the children.
 * 
 * @param {Object} props
 * @param {string} props.redirectPath - Path to redirect to if not authenticated
 * @param {string[]} [props.allowedRoles] - Roles allowed to access this route
 * @param {React.ReactNode} [props.children] - Children to render if authenticated
 * @returns {React.ReactNode} - The children or a redirect
 */
const ProtectedRoute = ({
  redirectPath = '/login',
  allowedRoles = [],
  children
}) => {
  const isLoggedIn = useIsLoggedIn();
  const activeRole = useSelector(selectActiveRole);
  // User data is now guaranteed to be available thanks to App.jsx initialization
  const primaryRole = useSelector(state => state.user?.profile?.data?.primaryRole || 'voiceActor');

  // Debug logging to track the fix
  console.log('ProtectedRoute Debug:', {
    isLoggedIn: !!isLoggedIn,
    activeRole,
    primaryRole,
    allowedRoles,
    userProfileExists: !!useSelector(state => state.user?.profile),
    userDataExists: !!useSelector(state => state.user?.profile?.data)
  });

  // Check if user is logged in (isLoggedIn returns token string or false)
  const userIsAuthenticated = Boolean(isLoggedIn);
  if (!userIsAuthenticated) {
    console.log('ProtectedRoute: User not authenticated, redirecting to login');
    return <SessionRedirect to={redirectPath} />;
  }

  // Check if user has required role (if specified)
  if (allowedRoles.length > 0) {
    // Check if any of the allowed roles are accessible to the user
    const hasAccess = allowedRoles.some(role => hasRoleAccess(primaryRole, role));
    console.log('ProtectedRoute: Role access check:', { hasAccess, primaryRole, allowedRoles });

    if (!hasAccess) {
      console.log('ProtectedRoute: Access denied, redirecting to role-specific landing');
      // Redirect to role-specific landing page
      const rolePaths = {
        admin: '/landing/admin',
        bookCreator: '/landing/book-creator',
        voiceActor: '/landing/voice-actor'
      };

      return <Navigate to={rolePaths[activeRole] || '/landing/admin'} replace />;
    }

    console.log('ProtectedRoute: Access granted, rendering component');
  }

  // If there are children, render them, otherwise render the Outlet
  return children ? children : <Outlet />;
};

export default ProtectedRoute;
